import { getMailPath } from 'owa-url';

/**
 * Return the deeplink url for client to open SxS view in mail
 * @param itemId of the related file
 * @param attachmentId of the related file
 * @return the deeplink url to open the file in SxS view
 */
export function getDeeplinkUrlForRelatedFile(
    itemId: string,
    attachmentId: string,
    previewUrl: string,
    isCloudyFile: boolean
): string {
    if (!itemId || !attachmentId) {
        return getDeeplinkUrlFromPreviewUrl(previewUrl, isCloudyFile);
    }

    return generateFileDeeplinkUrl(encodeURIComponent(itemId), encodeURIComponent(attachmentId));
}

/**
 * Get the react deeplink url from preview url by extracting the item id and attachment id
 * @param previewUrl of the related file
 * @param isCloudyFile whether the file is a cloudy file
 * @return the deeplink url to open the file in SxS view
 */
function getDeeplinkUrlFromPreviewUrl(previewUrl: string, isCloudyFile: boolean): string {
    if (!previewUrl) {
        return null;
    }

    // For implicit cloudy file, there is no itemId and attachmentId returned, so we directly use the preview url returned from OWS Prime.
    if (isCloudyFile) {
        return previewUrl;
    }

    // The previewUrl will be constructed in below format
    // https://outlook.office.com/owa/?viewmodel=IAttachmentViewModelPopoutFactory&AttachmentId=[ENCODED_ATTACHMENT_ID]&ItemId=[ENCODED_ITEM_ID]&AttachmentName=Folder+Permission.docx"
    const startIndexOfAttachmentId = previewUrl.indexOf('&AttachmentId=') + '&AttachmentId='.length;
    const endIndexOfAttachmentId = previewUrl.indexOf('&ItemId=');
    const startIndexOfItemId = endIndexOfAttachmentId + '&ItemId='.length;
    const endIndexOfItemId = previewUrl.indexOf('&AttachmentName=');

    // Validation
    if (
        startIndexOfAttachmentId >= endIndexOfAttachmentId ||
        endIndexOfAttachmentId >= startIndexOfItemId ||
        startIndexOfItemId >= endIndexOfItemId
    ) {
        // The server and client are not in an agreement of the format of the previewUrl. In this case, just return the JsMVVM deeplink to view the preview url
        // TODO log datapoint
        return previewUrl;
    }

    // Construct the string based on below url
    // https://outlook.office365.com/mail/deeplink/attachment/[item_id]/[attachment_id]
    const encodedAttachmentId = previewUrl.substring(
        startIndexOfAttachmentId,
        endIndexOfAttachmentId
    );
    const encodedItemId = previewUrl.substring(startIndexOfItemId, endIndexOfItemId);

    return generateFileDeeplinkUrl(encodedItemId, encodedAttachmentId);
}

/**
 * Generate the attachment email deeplink url based on the attachmentId and itemId
 * @param attachmentId of the related email
 * @param itemId of the related email
 * @return the deeplink url to open the email in SxS view
 */
export function getDeeplinkUrlForRelatedEmail(attachmentId: string, itemId: string): string {
    return (
        window.location.origin +
        getMailPath() +
        'deeplink/attachment/' +
        encodeURIComponent(itemId) +
        '/' +
        encodeURIComponent(attachmentId)
    );
}

/**
 * Generate the deeplink url based on the encodedAttachmentId and encodedItemId
 * @param encodedItemId of the related file
 * @param encodedAttachmentId of the related file
 * @return the deeplink url to open the file in SxS view
 */
function generateFileDeeplinkUrl(encodedItemId: string, encodedAttachmentId: string): string {
    return (
        window.location.origin +
        getMailPath() +
        'deeplink/attachment/' +
        encodedItemId +
        '/' +
        encodedAttachmentId
    );
}
